import { useCallback } from 'react'
import PropTypes from 'prop-types'
import { Collapse } from 'react-bootstrap'
import Link from 'components/link'
import classNames from 'classnames'
import css from './side-nav.scss'

const SideNav = ({
  activeSlugSet = [],
  categoryTree,
  maxDepth,
  navCategory,
  renderInMobileNav,
  sidebarTitle,
  onCategoryClick = () => {},
}) => (
  <nav className="hidden-xs">
    <div className="h6 text-bold mbs">{sidebarTitle}</div>
    <hr style={{ maxWidth: 200 }} />
    <ul
      className={classNames('catalog-side-nav list-unstyled', {
        [css.catalogMobileNav]: renderInMobileNav,
      })}
    >
      {[navCategory, ...categoryTree]
        .filter((category) => category && !category.hidden)
        .map((category) => (
          <CategoryWithChildren
            key={category.urlSlug}
            activeSlugSet={activeSlugSet}
            category={category}
            maxDepth={maxDepth}
            parentSlugSet={[]}
            onCategoryClick={onCategoryClick}
          />
        ))}
    </ul>
  </nav>
)

SideNav.propTypes = {
  activeSlugSet: PropTypes.arrayOf(PropTypes.string),
  categoryTree: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      children: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string,
          urlSlug: PropTypes.string,
          path: PropTypes.string,
          image: PropTypes.string,
        })
      ),
      path: PropTypes.string.isRequired,
      urlSlug: PropTypes.string.isRequired,
      image: PropTypes.string,
    })
  ).isRequired,
  maxDepth: PropTypes.number,
  navCategory: PropTypes.object,
  renderInMobileNav: PropTypes.bool,
  sidebarTitle: PropTypes.string.isRequired,
  onCategoryClick: PropTypes.func,
}

SideNav.defaultProps = {
  renderInMobileNav: false,
}

const CategoryWithChildren = ({
  activeSlugSet = [],
  category,
  depth = 1,
  isChild = false,
  maxDepth,
  parentSlugSet,
  onCategoryClick,
}) => {
  const { children = [], hidden, path, title, urlSlug } = category
  const leafCategory = !children || !children.length || depth === maxDepth
  const active = activeSlugSet.at(-1) === urlSlug
  const open = !leafCategory && activeSlugSet.includes(category.urlSlug)
  const currentSlugSet = [...parentSlugSet, urlSlug]

  const handleCategoryClick = useCallback(() => {
    onCategoryClick(category)
  }, [onCategoryClick, category])

  if (hidden) return null

  return (
    <li className={classNames(css.category, { [css.subCategory]: isChild })}>
      <Link
        className={classNames({ [css.active]: active })}
        to={{ pathname: path, state: { slugSet: currentSlugSet } }}
        onClick={handleCategoryClick}
      >
        {title}
        {!leafCategory && isChild && (
          <span className="pls">
            <span
              className={classNames('fa fa-angle-right', css.chevron, {
                [css.activeChevron]: open,
              })}
            />
          </span>
        )}
      </Link>
      {open && !leafCategory && (
        <Collapse in={open}>
          <ul className="list-unstyled pbs">
            {children
              .filter((child) => !child.hidden)
              .map((child) => (
                <CategoryWithChildren
                  key={child.urlSlug}
                  isChild
                  activeSlugSet={activeSlugSet}
                  category={child}
                  depth={depth + 1}
                  maxDepth={maxDepth}
                  parentSlugSet={currentSlugSet}
                  onCategoryClick={onCategoryClick}
                />
              ))}
          </ul>
        </Collapse>
      )}
    </li>
  )
}

CategoryWithChildren.propTypes = {
  activeSlugSet: PropTypes.arrayOf(PropTypes.string),
  category: PropTypes.object.isRequired,
  depth: PropTypes.number,
  isChild: PropTypes.bool,
  maxDepth: PropTypes.number,
  parentSlugSet: PropTypes.arrayOf(PropTypes.string),
  onCategoryClick: PropTypes.func,
}

export default SideNav
